import React, { useEffect, useMemo, useState } from "react";
import "./AddPolicyModal.css";
import { authFetch } from "../../utils/authFetch";
import CustomFieldsSection from "../custom-fields/CustomFieldsSection";

const PLAN_ID_COVERAGE_TYPES = ["MAPD", "PDP"];

const getPlanYearFromDate = (dateValue) => {
  if (!dateValue) return "";
  const year = new Date(`${dateValue}T00:00:00`).getFullYear();
  return Number.isNaN(year) ? "" : String(year);
};

const getCarrierId = (carrier) =>
  carrier?.id ?? carrier?.carrier_id ?? carrier?.carrierId ?? "";

const AddPolicyModal = ({ onClose, onSubmit, clientId = null, leadId = null }) => {
  const [step, setStep] = useState(1);

  const [coverageTypes, setCoverageTypes] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [policyStatuses, setPolicyStatuses] = useState([]);

  const [selectedCoverageType, setSelectedCoverageType] = useState("");
  const [selectedPlanTypeId, setSelectedPlanTypeId] = useState("");
  const [coverageTypeLoading, setCoverageTypeLoading] = useState(false);

  const [formData, setFormData] = useState({
    status: "",
    carrierId: "",
    planName: "",
    planId: "",
    benefitOption: "",
    benefitAmount: "",
    termLength: "",
    enrollmentPeriod: "",
    enrollmentCode: "",
    hraCompleted: "",
    applicationDate: "",
    effectiveDate: "",
    renewalDate: "",
    premium: "",
    policyNumber: "",
    faceValue: "",
  });

  const [customFieldValues, setCustomFieldValues] = useState([]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [coverageRes, statusesRes] = await Promise.all([
          authFetch("/api/coverage-types"),
          authFetch("/api/policy-statuses?type=policy"),
        ]);

        const coverageData = await coverageRes.json().catch(() => []);
        const statusesData = await statusesRes.json().catch(() => []);

        setCoverageTypes(Array.isArray(coverageData) ? coverageData : []);
        setPolicyStatuses(Array.isArray(statusesData) ? statusesData : []);
      } catch (err) {
        console.error("Load add-policy modal lookup data error:", err);
        setCoverageTypes([]);
        setPolicyStatuses([]);
        setCarriers([]);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    const loadCoverageTypeData = async () => {
      if (!selectedCoverageType) {
        setPlanTypes([]);
        setCarriers([]);
        setFormData((prev) => ({
          ...prev,
          carrierId: "",
        }));
        return;
      }

      try {
        setCoverageTypeLoading(true);

        const [planTypesRes, carriersRes] = await Promise.all([
          authFetch(`/api/coverage-types/${selectedCoverageType}/plan-types`),
          authFetch(`/api/subscriber-carriers/coverage-type/${selectedCoverageType}`),
        ]);

        const planTypesData = await planTypesRes.json().catch(() => []);
        const carriersData = await carriersRes.json().catch(() => ({}));

        const nextPlanTypes = Array.isArray(planTypesData) ? planTypesData : [];

        const nextCarriers = Array.isArray(carriersData?.carriers)
          ? carriersData.carriers
          : Array.isArray(carriersData)
            ? carriersData
            : [];

        setPlanTypes(nextPlanTypes);
        setCarriers(nextCarriers);

        setFormData((prev) => {
          if (!prev.carrierId) return prev;

          const carrierStillAvailable = nextCarriers.some(
            (carrier) => String(getCarrierId(carrier)) === String(prev.carrierId)
          );

          if (carrierStillAvailable) return prev;

          return {
            ...prev,
            carrierId: "",
            benefitOption: "",
            benefitAmount: "",
          };
        });
      } catch (err) {
        console.error("Load add-policy coverage type data error:", err);
        setPlanTypes([]);
        setCarriers([]);
        setFormData((prev) => ({
          ...prev,
          carrierId: "",
        }));
      } finally {
        setCoverageTypeLoading(false);
      }
    };

    loadCoverageTypeData();
  }, [selectedCoverageType]);

  const selectedTypeObj = useMemo(
    () => coverageTypes.find((ct) => Number(ct.id) === Number(selectedCoverageType)),
    [coverageTypes, selectedCoverageType]
  );

  const selectedTypeName = selectedTypeObj?.name || "";
  const shouldShowPlanId = PLAN_ID_COVERAGE_TYPES.includes(selectedTypeName);
  const shouldShowPlanName = selectedTypeName !== "Medicare Supplement";

  const selectedPlanType = useMemo(
    () => planTypes.find((pt) => String(pt.id) === String(selectedPlanTypeId)),
    [planTypes, selectedPlanTypeId]
  );

  const selectedCarrier = useMemo(
    () => carriers.find((c) => String(getCarrierId(c)) === String(formData.carrierId)),
    [carriers, formData.carrierId]
  );

  const needsPlanTypeStep = planTypes.length > 0;

  const currentStepTitle =
    step === 1
      ? "Select Coverage Type"
      : step === 2
      ? selectedTypeName === "Individual Health"
        ? "Choose Individual Health Type"
        : selectedTypeName === "Life"
        ? "Choose Life Insurance Type"
        : selectedTypeName === "Medicare Supplement"
        ? "Choose Medicare Supplement Plan Type"
        : "Choose Plan Type"
      : "Policy Details";

  const currentStepSubtitle =
    step === 1
      ? "Choose the policy category to tailor the rest of the setup flow."
      : step === 2
      ? "Select the plan type before entering carrier, dates, and policy details."
      : "Complete the policy setup with carrier, plan details, dates, and enrollment details.";

  const handleCarrierChange = (e) => {
    const { value } = e.target;

    setFormData((prev) => ({
      ...prev,
      carrierId: value,
      benefitOption: "",
      benefitAmount: "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (name === "planName") {
        return {
          ...prev,
          planName: value,
        };
      }

      if (name === "effectiveDate") {
        return {
          ...prev,
          effectiveDate: value,
        };
      }

      return { ...prev, [name]: value };
    });
  };

  const handleNextFromStep1 = () => {
    if (!selectedCoverageType || coverageTypeLoading) return;
    setStep(needsPlanTypeStep ? 2 : 3);
  };

  const handleNextFromPlanType = () => {
    if (!selectedPlanTypeId) return;
    setStep(3);
  };

  const renderCarrierSelect = () => (
    <div className="form-group">
      <label>Carrier</label>
      <select
        name="carrierId"
        value={formData.carrierId}
        onChange={handleCarrierChange}
        disabled={!selectedCoverageType || coverageTypeLoading || carriers.length === 0}
      >
        <option value="">
          {coverageTypeLoading
            ? "-- Loading carriers --"
            : carriers.length === 0
              ? "-- No carriers configured --"
              : "-- Select Carrier --"}
        </option>
        {carriers.map((c) => {
          const carrierId = getCarrierId(c);

          return (
            <option key={carrierId} value={carrierId}>
              {c.name || c.carrier_name}
            </option>
          );
        })}
      </select>

      {selectedCoverageType && !coverageTypeLoading && carriers.length === 0 && (
        <small>
          No carriers have been selected for this coverage type. Manage carriers in Settings.
        </small>
      )}
    </div>
  );

  const renderPlanNameInput = () => (
    <div className="form-group">
      <label>Plan Name</label>
      <input
        type="text"
        name="planName"
        value={formData.planName}
        onChange={handleChange}
        placeholder="Enter plan/product name"
      />
    </div>
  );

  const renderPlanIdInput = () =>
    shouldShowPlanId ? (
      <div className="form-group">
        <label>Plan ID</label>
        <input
          type="text"
          name="planId"
          value={formData.planId}
          onChange={handleChange}
          placeholder="Example: H1234-001"
        />
      </div>
    ) : null;

  const renderCoverageSpecificFields = () => {
    switch (selectedTypeName) {
      case "Dental":
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}
          </>
        );

      case "MAPD":
      case "PDP":
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}
            {renderPlanIdInput()}

            <div className="form-group">
              <label>Enrollment Period</label>
              <select
                name="enrollmentPeriod"
                value={formData.enrollmentPeriod}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="IEP">IEP</option>
                <option value="ICEP">ICEP</option>
                <option value="OEP">OEP</option>
                <option value="AEP">AEP</option>
                <option value="SEP">SEP</option>
              </select>
            </div>

            {selectedTypeName === "MAPD" && (
              <div className="form-group">
                <label>HRA Completed</label>
                <select name="hraCompleted" value={formData.hraCompleted} onChange={handleChange}>
                  <option value="">-- Select --</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            )}
          </>
        );

      case "Medicare Supplement":
        return (
          <>
            <div className="form-group">
              <label>Plan Type</label>
              <input value={selectedPlanType?.name || ""} readOnly />
            </div>

            {renderCarrierSelect()}

            <div className="form-group">
              <label>Enrollment Period</label>
              <select
                name="enrollmentPeriod"
                value={formData.enrollmentPeriod}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="Open Enrollment">Open Enrollment</option>
                <option value="Underwritten">Underwritten</option>
                <option value="Guaranteed Issue">Guaranteed Issue</option>
              </select>
            </div>
          </>
        );

      case "Life":
        return (
          <>
            <div className="form-group">
              <label>Plan Type</label>
              <input value={selectedPlanType?.name || ""} readOnly />
            </div>

            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}

            <div className="form-group">
              <label>Face Value</label>
              <input
                type="number"
                name="faceValue"
                value={formData.faceValue}
                onChange={handleChange}
              />
            </div>
          </>
        );

      case "Individual Health":
        return (
          <>
            <div className="form-group">
              <label>Plan Type</label>
              <input value={selectedPlanType?.name || ""} readOnly />
            </div>

            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}

            {selectedPlanType?.name === "Short-Term Health" && (
              <>
                <div className="form-group">
                  <label>Benefit Amount</label>
                  <input
                    type="number"
                    name="benefitAmount"
                    value={formData.benefitAmount}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Term Length (months)</label>
                  <input
                    type="number"
                    name="termLength"
                    value={formData.termLength}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}
          </>
        );

      default:
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}
          </>
        );
    }
  };

  const handleSubmit = async () => {
    const payload = {
      status: formData.status || null,
      carrierId: parseInt(formData.carrierId, 10) || null,
      carrier_id: parseInt(formData.carrierId, 10) || null,
      productId: null,
      product_id: null,
      planName: shouldShowPlanName ? formData.planName || null : null,
      plan_name: shouldShowPlanName ? formData.planName || null : null,
      planId: shouldShowPlanId ? formData.planId || null : null,
      plan_id: shouldShowPlanId ? formData.planId || null : null,
      coverageTypeId: parseInt(selectedCoverageType, 10) || null,
      coverage_type_id: parseInt(selectedCoverageType, 10) || null,
      coverageTypePlanTypeId: selectedPlanTypeId ? parseInt(selectedPlanTypeId, 10) : null,
      coverage_type_plan_type_id: selectedPlanTypeId ? parseInt(selectedPlanTypeId, 10) : null,
      applicationDate: formData.applicationDate || null,
      application_date: formData.applicationDate || null,
      effectiveDate: formData.effectiveDate || null,
      effective_date: formData.effectiveDate || null,
      renewalDate: formData.renewalDate || null,
      renewal_date: formData.renewalDate || null,
      terminationDate: null,
      termination_date: null,
      premium: formData.premium || null,
      policyNumber: formData.policyNumber || null,
      policy_number: formData.policyNumber || null,
      enrollmentCode: formData.enrollmentCode || null,
      enrollment_code: formData.enrollmentCode || null,
      electionPeriod: formData.enrollmentPeriod || null,
      election_period: formData.enrollmentPeriod || null,
      hraCompleted: formData.hraCompleted || null,
      hra_completed: formData.hraCompleted || null,
      benefitOption: formData.benefitOption || null,
      benefit_option: formData.benefitOption || null,
      faceValue: formData.faceValue || null,
      face_value: formData.faceValue || null,
      benefitAmount: formData.benefitAmount || null,
      benefit_amount: formData.benefitAmount || null,
      termLength: formData.termLength || null,
      term_length: formData.termLength || null,
      planYear: getPlanYearFromDate(formData.effectiveDate) || null,
      plan_year: getPlanYearFromDate(formData.effectiveDate) || null,
      clientId,
      client_id: clientId,
      leadId,
      lead_id: leadId,
      recordType: "policy",
      record_type: "policy",
    };

    try {
      const createdPolicy = await onSubmit(payload);

      const createdPolicyId =
        createdPolicy?.id ||
        createdPolicy?.policy?.id ||
        createdPolicy?.insertId;

      if (createdPolicyId && customFieldValues.length > 0) {
        const customFieldRes = await authFetch(
          `/api/custom-fields/values/policy/${createdPolicyId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              values: customFieldValues,
            }),
          }
        );

        const customFieldData = await customFieldRes.json().catch(() => ({}));

        if (!customFieldRes.ok) {
          throw new Error(customFieldData.error || "Policy created, but custom fields failed to save.");
        }
      }

      onClose();
    } catch (err) {
      console.error("Add policy error:", err);
      alert(err.message || "Failed to add policy.");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-lg add-policy-modal redesigned-add-policy-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-shell">
          <div className="modal-header add-policy-header">
            <div>
              <h3>{currentStepTitle}</h3>
              <p className="modal-subtitle">{currentStepSubtitle}</p>
            </div>

            <button type="button" className="modal-close-btn" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="modal-body add-policy-body">
            {step === 1 && (
              <div className="add-policy-panel">
                <div className="add-policy-panel-head">
                  <h4>Select Coverage Type</h4>
                  <p>Choose the policy category to begin.</p>
                </div>

                <div className="add-policy-panel-body">
                  <div className="form-group add-policy-coverage-select">
                    <label>Coverage Type</label>
                    <select
                      value={selectedCoverageType}
                      onChange={(e) => {
                        setSelectedCoverageType(e.target.value);
                        setSelectedPlanTypeId("");
                        setStep(1);
                        setPlanTypes([]);
                        setCarriers([]);
                        setFormData((prev) => ({
                          ...prev,
                          carrierId: "",
                          planName: "",
                          planId: "",
                          benefitOption: "",
                          benefitAmount: "",
                          termLength: "",
                          enrollmentPeriod: "",
                          enrollmentCode: "",
                          hraCompleted: "",
                          faceValue: "",
                        }));
                      }}
                    >
                      <option value="">-- Select --</option>
                      {coverageTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="add-policy-panel">
                <div className="add-policy-panel-head">
                  <h4>{currentStepTitle}</h4>
                  <p>Select the plan type before entering detailed policy information.</p>
                </div>

                <div className="add-policy-panel-body">
                  <div className="form-group add-policy-coverage-select">
                    <label>
                      {selectedTypeName === "Life"
                        ? "Life Insurance Type"
                        : selectedTypeName === "Individual Health"
                        ? "Individual Health Type"
                        : selectedTypeName === "Medicare Supplement"
                        ? "Medicare Supplement Plan Type"
                        : "Plan Type"}
                    </label>
                    <select
                      value={selectedPlanTypeId}
                      onChange={(e) => setSelectedPlanTypeId(e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      {planTypes.map((pt) => (
                        <option key={pt.id} value={pt.id}>
                          {pt.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <>
                <div className="add-policy-summary-row">
                  <div className="add-policy-summary-card">
                    <div className="add-policy-summary-label">Coverage</div>
                    <div className="add-policy-summary-value">{selectedTypeName || "Not Set"}</div>
                  </div>

                  <div className="add-policy-summary-card">
                    <div className="add-policy-summary-label">Plan Type</div>
                    <div className="add-policy-summary-value">
                      {selectedPlanType?.name || "Not Set"}
                    </div>
                  </div>

                  <div className="add-policy-summary-card">
                    <div className="add-policy-summary-label">Carrier</div>
                    <div className="add-policy-summary-value">
                      {selectedCarrier?.name || selectedCarrier?.carrier_name || "Not Set"}
                    </div>
                  </div>

                  {shouldShowPlanName && (
                    <div className="add-policy-summary-card">
                      <div className="add-policy-summary-label">Plan</div>
                      <div className="add-policy-summary-value">
                        {formData.planName || "Not Set"}
                      </div>
                    </div>
                  )}
                </div>

                <div className="add-policy-panel">
                  <div className="add-policy-panel-head">
                    <h4>Policy Details</h4>
                    <p>Complete the policy setup with carrier, dates, premium, and plan details.</p>
                  </div>

                  <div className="add-policy-panel-body">
                    <div className="add-policy-form-grid">
                      <div className="form-group">
                        <label>Status</label>
                        <select name="status" value={formData.status} onChange={handleChange}>
                          <option value="">-- Select --</option>
                          {policyStatuses.map((s) => (
                            <option key={s.id} value={s.name}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Application Date</label>
                        <input
                          type="date"
                          name="applicationDate"
                          value={formData.applicationDate}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Effective Date</label>
                        <input
                          type="date"
                          name="effectiveDate"
                          value={formData.effectiveDate}
                          onChange={handleChange}
                        />
                      </div>

                      {renderCoverageSpecificFields()}

                      <div className="form-group">
                        <label>Renewal Date</label>
                        <input
                          type="date"
                          name="renewalDate"
                          value={formData.renewalDate}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Premium</label>
                        <input
                          type="number"
                          name="premium"
                          value={formData.premium}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Policy Number</label>
                        <input
                          type="text"
                          name="policyNumber"
                          value={formData.policyNumber}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Enrollment Code</label>
                        <input
                          type="text"
                          name="enrollmentCode"
                          value={formData.enrollmentCode}
                          onChange={handleChange}
                        />
                      </div>

                      <CustomFieldsSection
                        recordType="policy"
                        onValuesChange={setCustomFieldValues}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer add-policy-footer">
            <div className="modal-footer-left" />
            <div className="modal-footer-right">
              {step === 1 && (
                <>
                  <button type="button" className="modal-button" onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="modal-button save"
                    disabled={!selectedCoverageType || coverageTypeLoading}
                    onClick={handleNextFromStep1}
                  >
                    {coverageTypeLoading ? "Loading..." : "Continue"}
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <button type="button" className="modal-button" onClick={() => setStep(1)}>
                    Back
                  </button>
                  <button
                    type="button"
                    className="modal-button save"
                    disabled={!selectedPlanTypeId}
                    onClick={handleNextFromPlanType}
                  >
                    Continue
                  </button>
                </>
              )}

              {step === 3 && (
                <>
                  <button
                    type="button"
                    className="modal-button"
                    onClick={() => setStep(needsPlanTypeStep ? 2 : 1)}
                  >
                    Back
                  </button>
                  <button type="button" className="modal-button save" onClick={handleSubmit}>
                    Add {selectedTypeName || "Policy"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPolicyModal;